<template>
  <div v-if="valuePropsData" :class="rootClasses">
    <HorizontalDragScroll :class="[valuePropsData.verticalStyle ? 'vertical-value-props' : 'value-props']" :snap="!valuePropsData.verticalStyle">
      <div v-if="mainTitle && valuePropsData.verticalStyle" class="container-main-text">
        <span class="main-text">{{ mainTitle }}</span>
      </div>
      <template v-for="(item, index) in valuePropsData.items" :key="item.text">
        <div class="content">
          <article class="tile align-center">
            <div class="icon-img">
              <suspense>
                <ContentfulAsset v-bind="item.icon" :link="item.link" />
              </suspense>
            </div>
            <div class="icon-text">
              <TextTemplate :template="item.text" :isMarkdown="true" @loaded="onLoad">
                <template v-slot:dialog="{ slug }">
                  <a class="accent" role="button" @click="showModal(slug.split('|')[1])">
                    {{ slug.split('|')[0] }}
                  </a>
                </template>
              </TextTemplate>
            </div>
          </article>
        </div>
        <hr class="vertical-divider" v-if="index < valuePropsData.items.length - 1 && !valuePropsData.verticalStyle" />
      </template>
    </HorizontalDragScroll>
  </div>
</template>

<script>
import { useContentful, contentfulProps } from '@/compositions/useContentful.js';
import { computed, defineAsyncComponent } from 'vue';
import ContentfulAsset from '@/components/contentful/Fields/ContentfulAsset.vue';
import HorizontalDragScroll from '@/components/commons/HorizontalDragScroll.vue';
import TextTemplate from '@/components/commons/TextTemplate.vue';
import { openModal } from '@/components/commons/Dialog/Dialog.vue';

export default {
  components: {
    ContentfulAsset,
    HorizontalDragScroll,
    TextTemplate,
  },
  props: contentfulProps,
  async setup(p, c) {
    const {
      data: { value },
      init,
    } = useContentful('valueProps', p, c);
    const { icon1, icon2, icon3, icon4, text1, text2, text3, text4, fullWidth, mainTitle, verticalStyle } = value;

    const valuePropsData = computed(() => {
      if (!value) {
        return '';
      }
      const items = [
        {
          id: 'icon-1',
          icon: icon1,
          text: text1,
        },
        {
          id: 'icon-2',
          icon: icon2,
          text: text2,
        },
        {
          id: 'icon-3',
          icon: icon3,
          text: text3,
        },
      ];

      if (icon4 && text4) {
        items.push({
          id: 'icon-4',
          icon: icon4,
          text: text4,
        });
      }

      return { items, fullWidth, verticalStyle };
    });

    const rootClasses = computed(() => {
      const classes = ['container-value-props'];
      if (value.fullWidth) classes.push('bleedout', 'to-edge');
      return classes;
    });

    const showModal = async (slug) => {
      return openModal(
        defineAsyncComponent(() => import('./ContentfulSection.vue')),
        { slug, showLoader: true },
      );
    };

    const onLoad = (slugs) => {
      if (!slugs || slugs.length == 0) return;

      // slug will look like: some-text|slug-to-content
      slugs = slugs.map((slug) => slug.split('|')[1]);
      const uniqueSlugs = Array.from(new Set(slugs));

      // get slugs and put them in cache.
      setTimeout(() => {
        uniqueSlugs.forEach((slug) => {
          useContentful('pageComponentSection', { slug }).init();
        });
      }, 500);
    };

    await init();
    return {
      valuePropsData,
      rootClasses,
      showModal,
      onLoad,
      mainTitle,
    };
  },
};
</script>

<style lang="postcss">
@import '@/pixel/style/variables';
@import '@/pixel/style/mixins';

.container-value-props {
  .value-props {
    display: flex;
    padding-top: var(--space-5);
    padding-bottom: var(--space-5);
    overflow-x: auto;
    max-width: 2560px;
    margin: auto;
    @mixin responsiveContainerPadding;

    .vertical-divider {
      height: var(--space-10);
      width: 0;
      border: 2px solid var(--grey-ultra-light);
      display: flex;
      align-self: center;
      margin-left: var(--space-10);
      margin-right: var(--space-10);
    }

    .content {
      display: flex;
      flex: 1;
      min-width: 180px;
      scroll-snap-align: center;

      .tile {
        display: flex;
        flex-direction: column;
        align-items: center;
        font-size: var(--font-size-20);
        width: 100%;

        .icon-img {
          width: 2em;
          height: 2em;
          min-width: 40px;
          min-height: 40px;
        }
        .icon-text {
          letter-spacing: normal;
          text-transform: none;
          line-height: 1.25em;
          font-family: var(--font-regular);
          color: var(--grey-medium);
          margin-top: var(--space-5);
          flex: 1;
          max-width: 10em;

          @media (max-width: calc($desktop-breakpoint - 1px)) {
            text-align: center;
          }

          @media (min-width: $desktop-breakpoint) {
            margin-top: 0;
            margin-left: var(--space-8);
          }

          @media (max-width: calc($mobile-breakpoint - 1px)) {
            max-width: 176px;
          }
        }

        @media (max-width: calc($mobile-breakpoint - 1px)) {
          font-size: var(--font-size-16);
        }

        @media (min-width: $large-breakpoint) {
          font-size: var(--font-size-24);
        }

        @media (min-width: $xxl-breakpoint) {
          font-size: var(--font-size-32);
        }

        @media (min-width: $desktop-breakpoint) {
          flex-direction: row;
          flex-wrap: wrap;
        }

        &.align-center {
          justify-content: center;
        }
      }
    }
  }

  .vertical-value-props {
    width: inherit;
    border: solid 1px var(--grey-light);
    border-radius: var(--space-2);

    @media (max-width: 360px) {
      width: auto;
    }

    .container-main-text {
      margin-bottom: var(--space-5);
      margin-top: var(--space-5);
    }
    .main-text {
      margin: 0px var(--space-8) 0px var(--space-8);
      font-size: var(--space-5);
    }
    .content {
      display: flex;
      flex: 1;
      width: auto;
      line-height: var(--space-6);
      margin: 10px var(--space-5) var(--space-5) var(--space-8);
    }

    .tile {
      display: flex;
      font-size: var(--font-size-16);
      flex-direction: row;
      flex-wrap: wrap;
    }

    .icon-img {
      width: var(--space-7);
      height: var(--space-7);
      margin-right: 10px;
    }

    .icon-text {
      flex: 1;
      line-height: var(--space-6);
    }
  }
}
</style>
